// src/components/Navbar.js

import React, { useContext } from 'react';
import { AppBar, Toolbar, Typography, Button, Box } from '@mui/material';
import { styled } from '@mui/system';
import DiscordLoginButton from './DiscordLoginButton';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import mindguardLogo from '../assets/mindguard_logo_final.png'; // Import the logo

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'rgba(0, 0, 0, 0.5)', // Dark transparent color to match HeroSection
  boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)', // Light shadow for better integration
  position: 'fixed', // Fix it at the top of the page
  top: 0,
  left: 0,
  right: 0,
  zIndex: 1300, // Ensure it's above other elements on the page
  padding: theme.spacing(0.5, 3), // Less padding for a thinner navbar
  height: '56px', // Adjust the height to make it thinner
}));

const NavbarContainer = styled(Toolbar)(({ theme }) => ({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between', // Spread items across the navbar
  alignItems: 'center',
  maxWidth: '2000px', // Update max width as per your preference
  margin: '0 auto', // Center the navbar content horizontally
}));

const TitleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

const Title = styled(Typography)(({ theme }) => ({
  color: '#FFFFFF',
  fontFamily: 'Poppins, sans-serif',
  fontWeight: 700,
}));

const Logo = styled('img')(({ theme }) => ({
  width: '28px', // Slightly smaller logo to fit the thinner navbar
  height: '28px',
  marginRight: theme.spacing(1),
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2), // Add gap between buttons
}));

// Updated NavbarButton for glowing effect
const NavbarButton = styled(Button)(({ theme }) => ({
  color: '#FFFFFF',
  textTransform: 'none',
  fontFamily: 'Poppins, sans-serif',
  borderRadius: '20px',
  padding: '6px 16px',
  marginLeft: theme.spacing(2),
  background: 'linear-gradient(to right, #9AC0CD, #4C5C64)', // Gradient similar to the glow color
  boxShadow: '0 4px 15px rgba(154, 192, 205, 0.5)', // Glow effect
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: '#4C5C64', // Hover effect to darken the color
    boxShadow: '0 4px 20px rgba(154, 192, 205, 0.7)', // Increased glow on hover
  },
}));

const Navbar = () => {
  const navigate = useNavigate();
  const { user, logout } = useContext(AuthContext);

  const handleSetupClick = () => {
    if (user) {
      // If the user is logged in, navigate to /setup
      navigate('/setup');
    } else {
      // If the user is not logged in, navigate to the login page
      // Here, assuming DiscordLoginButton initiates login
      alert('Please login to access the setup page.');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/'); // Redirect to home after logout
  };

  return (
    <StyledAppBar position="static">
      <NavbarContainer>
        {/* Logo and Title positioned to the left */}
        <TitleContainer>
          <Logo src={mindguardLogo} alt="MINDguard Logo" />
          <Title variant="h6">MINDguard</Title>
        </TitleContainer>

        {/* Button container positioned to the right */}
        <ButtonContainer>
          {/* Always show the Setup button */}
          <NavbarButton
            variant="contained" // Changed to 'contained' to make it more prominent
            onClick={handleSetupClick}
          >
            Setup
          </NavbarButton>

          {/* Conditionally render Logout button or DiscordLoginButton */}
          {user ? (
            <NavbarButton
              variant="contained" // Changed to 'contained' to match the style
              onClick={handleLogout}
            >
              Logout
            </NavbarButton>
          ) : (
            <DiscordLoginButton />
          )}
        </ButtonContainer>
      </NavbarContainer>
    </StyledAppBar>
  );
};

export default Navbar;

